import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { LogOut } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Espace partenaire : liens personnels à partager, code partenaire, résultats agrégés
// et avantages offerts par Julie. AUCUNE donnée personnelle des utilisatrices n'y figure.
export default function PartnerSpace() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const token = localStorage.getItem("rav-token");
    if (!token) {
      navigate("/connexion?mode=login");
      return;
    }
    axios
      .get(`${API}/partners/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setData(data))
      .catch((err) => setError(err.response?.data?.detail || "Votre espace partenaire n'est pas accessible."));
  }, [navigate]);

  const logout = () => {
    localStorage.removeItem("rav-token");
    navigate("/");
  };

  const linkUrl = (slug) => `${window.location.origin}/p/${slug}`;
  const copy = (text) => {
    navigator.clipboard?.writeText(text);
  };

  if (error) {
    return (
      <main className="rav-page" data-testid="partner-space-error" style={{ textAlign: "center" }}>
        <img src="/logo-accueil.webp" alt="Respire Aime Vibre" className="rav-page-logo" />
        <p className="rav-page-sub">{error}</p>
        <button type="button" className="rav-primary-button" data-testid="partner-space-login" onClick={() => navigate("/connexion?mode=login")}>
          Me connecter
        </button>
      </main>
    );
  }
  if (!data) return <main className="rav-page" data-testid="partner-space-loading"><p className="rav-page-sub">Chargement de votre espace…</p></main>;

  const { partner, status, active, links, stats, benefits } = data;

  return (
    <main className="rav-page" data-testid="partner-space">
      <img src="/logo-accueil.webp" alt="Respire Aime Vibre" className="rav-page-logo" />
      <h1 className="rav-page-title" style={{ textAlign: "center" }}>
        Espace partenaire{partner.name ? ` — ${partner.name}` : ""}
      </h1>
      <p className="rav-page-sub" data-testid="partner-space-status" style={{ textAlign: "center" }}>
        Référence {partner.pid} · Statut : {status || "—"}
      </p>

      {!active ? (
        <div className="rav-limit-card" data-testid="partner-space-pending" style={{ textAlign: "center" }}>
          <p style={{ fontWeight: 600, margin: 0 }}>Votre espace est en cours de validation</p>
          <p className="rav-page-sub" style={{ margin: "6px 0 0" }}>
            Julie examine personnellement chaque demande de partenariat. Vous recevrez un e-mail dès l'activation :
            vos liens personnels et vos résultats apparaîtront alors ici.
          </p>
        </div>
      ) : (
        <>
          <h2 className="rav-admin-h2">Vos liens à partager</h2>
          <p className="rav-page-sub">
            Partagez ces liens sur vos réseaux, votre site ou lors de vos événements. Votre code partenaire : <strong data-testid="partner-space-code">{partner.code}</strong><br />
            À chaque partage, l'aperçu affiche automatiquement le logo et le slogan de Respire Aime Vibre — « L'Instant qui vous ressemble ».
          </p>
          <div className="rav-admin-list">
            {links.map((l, i) => (
              <div key={l.id} className="rav-admin-row" data-testid={`partner-link-row-${i}`}>
                <span className="rav-admin-row-label">{l.name}</span>
                <span className="rav-admin-row-sub">{linkUrl(l.slug)}</span>
                <span className="rav-admin-row-actions">
                  <button type="button" className="rav-text-link" data-testid={`partner-link-copy-${i}`} onClick={() => copy(linkUrl(l.slug))}>
                    Copier
                  </button>
                  <button type="button" className="rav-text-link" data-testid={`partner-link-share-${i}`} onClick={() => copy(`Respire Aime Vibre — L'Instant qui vous ressemble 🤎\nOffrez-vous un instant de sophrologie personnalisé : ${linkUrl(l.slug)}`)}>
                    Message de partage
                  </button>
                </span>
              </div>
            ))}
          </div>

          {stats && (
            <>
              <h2 className="rav-admin-h2">Vos résultats (depuis le début)</h2>
              <div className="rav-admin-grid">
                <div className="rav-admin-card" data-testid="pstat-clicks"><div className="rav-admin-card-value">{stats.clicks}</div><div className="rav-admin-card-label">Clics sur vos liens</div></div>
                <div className="rav-admin-card" data-testid="pstat-visitors"><div className="rav-admin-card-value">{stats.unique_visitors}</div><div className="rav-admin-card-label">Visiteurs uniques</div></div>
                <div className="rav-admin-card" data-testid="pstat-signups"><div className="rav-admin-card-value">{stats.signups}</div><div className="rav-admin-card-label">Personnes inscrites</div></div>
                <div className="rav-admin-card" data-testid="pstat-premium"><div className="rav-admin-card-value">{stats.premium}</div><div className="rav-admin-card-label">Abonnements RAV+</div></div>
              </div>
            </>
          )}

          {benefits.length > 0 && (
            <>
              <h2 className="rav-admin-h2">Vos avantages Respire Aime Vibre</h2>
              <div className="rav-admin-list">
                {benefits.map((b, i) => (
                  <div key={i} className="rav-admin-row" data-testid={`partner-benefit-${i}`}>
                    <span className="rav-admin-row-label">{b.label}</span>
                    <span className="rav-admin-row-sub">
                      {b.gift_until ? `Jusqu'au ${new Date(b.gift_until).toLocaleDateString("fr-FR")}` : ""} {b.status === "actif" ? "· actif" : ""}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}

      <button type="button" className="rav-secondary-button rav-admin-logout" data-testid="partner-space-logout" onClick={logout} style={{ marginTop: 24 }}>
        <LogOut size={14} strokeWidth={1.6} /> Se déconnecter
      </button>
    </main>
  );
}
